<script setup>
import { ref } from 'vue'
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  estimation:            { type: Object,  default: () => ({}) },
  projet:                { type: Object,  default: () => ({}) },
  partie_suivie:         { type: Object,  default: () => ({}) },
  configuration_imports: { type: Array,   default: () => [] },
  errors:                { type: Object,  default: () => ({}) },
  back_url:              { type: String,  default: '#' },
  submit_url:            { type: String,  default: '#' },
})

const form = useForm({
  estimation_partie_suivie_element_import: {
    file: null,
    estimation_partie_suivie_id: props.partie_suivie.id,
  },
})
const { error: toastError } = useToast()
const dragOver = ref(false)
const fileName = ref(null)

function onFile(e) {
  const file = e.target.files?.[0] || e.dataTransfer?.files?.[0]
  if (!file) return
  form.estimation_partie_suivie_element_import.file = file
  fileName.value = file.name
}

function onDrop(e) {
  dragOver.value = false
  onFile(e)
}

function clearFile() {
  form.estimation_partie_suivie_element_import.file = null
  fileName.value = null
}

function submit() {
  if (!form.estimation_partie_suivie_element_import.file) { toastError('Veuillez sélectionner un fichier.'); return }
  form.post(props.submit_url, {
    forceFormData: true,
    preserveScroll: true,
    onError: () => {
      const merged = { ...props.errors, ...form.errors }
      Object.entries(merged).forEach(([k, v]) => {
        const msgs = Array.isArray(v) ? v : [v]
        msgs.forEach(m => toastError(m))
      })
    },
  })
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="flex items-center gap-3">
        <a :href="back_url" class="text-outline hover:text-on-surface transition-colors">
          <span class="material-symbols-outlined text-xl">arrow_back</span>
        </a>
        <span class="text-outline">/</span>
        <a :href="projet.url" class="font-mono text-sm font-bold text-primary hover:brightness-125">{{ projet.code }}</a>
        <span class="text-outline">/</span>
        <a :href="estimation.url" class="font-mono text-sm font-bold text-primary hover:brightness-125">{{ estimation.code }}</a>
        <span class="text-outline">/</span>
        <span class="font-mono text-sm font-bold text-primary">{{ partie_suivie.code }}</span>
        <span class="text-outline">/</span>
        <span class="text-xs font-black uppercase tracking-widest text-on-surface">Importer éléments</span>
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-4xl mx-auto">

        <!-- Page Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
              Importer les éléments — {{ partie_suivie.code }}
            </h1>
            <p class="text-outline text-sm mt-1">
              Projet <span class="font-mono font-bold text-primary">{{ projet.code }}</span>
              — Estimation <span class="font-mono font-bold text-primary">{{ estimation.code }}</span>
              — Partie <span class="font-mono font-bold text-primary">{{ partie_suivie.code }}</span>
              — Importation par fichier CSV ou Excel
            </p>
          </div>
          <a
            :href="back_url"
            class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
          >
            <span class="material-symbols-outlined text-base">arrow_back</span>
            Retour
          </a>
        </div>

        <!-- Upload Card -->
        <div class="bg-surface-container rounded-xl p-8 border-l-4 border-primary shadow-2xl shadow-black/40 mb-8">
          <form @submit.prevent="submit" class="space-y-6">

            <!-- Drop Zone -->
            <div>
              <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                Fichier d'importation <span class="text-error">*</span>
              </label>
              <div class="relative">
              <label
                @dragover.prevent="dragOver = true"
                @dragleave="dragOver = false"
                @drop.prevent="onDrop"
                :class="[
                  'flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-xl p-10 cursor-pointer transition-all',
                  dragOver
                    ? 'border-primary bg-primary/5'
                    : fileName
                      ? 'border-tertiary/50 bg-tertiary/5'
                      : 'border-outline-variant/30 bg-surface-container-highest/40 hover:border-primary/40 hover:bg-primary/5'
                ]"
              >
                <div class="relative">
                  <span
                    :class="fileName ? 'text-tertiary' : 'text-outline/40'"
                    class="material-symbols-outlined text-5xl"
                  >{{ fileName ? 'task' : 'upload_file' }}</span>
                  <button
                    v-if="fileName"
                    type="button"
                    @click.prevent.stop="clearFile"
                    class="absolute -top-1.5 -right-4 w-5 h-5 flex items-center justify-center rounded-full bg-surface-container border border-outline-variant/30 text-outline hover:text-error hover:border-error/40 transition-all shadow"
                    title="Supprimer le fichier"
                  >
                    <span class="material-symbols-outlined text-xs">close</span>
                  </button>
                </div>

                <div class="text-center">
                  <p v-if="fileName" class="text-sm font-black text-on-surface">{{ fileName }}</p>
                  <p v-else class="text-sm font-bold text-outline">
                    Glisser-déposer un fichier ou <span class="text-primary underline underline-offset-2">parcourir</span>
                  </p>
                  <p class="text-[10px] text-outline/60 uppercase tracking-widest mt-1">CSV · XLS · XLSX</p>
                </div>

                <input
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  class="hidden"
                  @change="onFile"
                />
              </label>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-4 pt-2">
              <button
                type="submit"
                :disabled="form.processing || !form.estimation_partie_suivie_element_import.file"
                class="flex-1 machined-sheen text-on-primary font-black py-4 rounded-md hover:brightness-110 active:scale-[0.98] transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase tracking-widest text-sm"
              >
                <span v-if="form.processing" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
                <span v-else class="material-symbols-outlined text-base">upload</span>
                Lancer l'importation
              </button>
              <a
                :href="back_url"
                class="px-8 py-4 bg-transparent border border-outline-variant/30 text-outline font-black rounded-md hover:bg-surface-container-highest hover:text-on-surface transition-all uppercase tracking-widest text-sm"
              >
                Annuler
              </a>
            </div>

          </form>
        </div>

        <!-- Configuration Table -->
        <div v-if="configuration_imports.length > 0" class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">Format de fichier attendu</h2>
          </div>
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="border-b border-outline-variant/10">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-40">Colonne</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-48">Format</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Description</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-24 text-center">Requis</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr
                v-for="ci in configuration_imports"
                :key="ci.id"
                class="hover:bg-surface-container-highest/20 transition-colors"
              >
                <td class="px-6 py-3">
                  <code class="text-xs font-black text-primary font-mono">{{ ci.import_file_column_name }}</code>
                </td>
                <td class="px-6 py-3 text-xs text-outline">{{ ci.format_label }}</td>
                <td class="px-6 py-3 text-xs text-on-surface/80">{{ ci.description }}</td>
                <td class="px-6 py-3 text-center">
                  <span
                    v-if="ci.mandatory"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-error/10 text-error border border-error/20"
                  >Requis</span>
                  <span
                    v-else
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-outline/10 text-outline border border-outline/10"
                  >Optionnel</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>
  </AppLayout>
</template>
